import { Box, Table, TableBody } from '@mui/material';
import Pagination from 'components/controls/Pagination';
import TableHead from 'components/controls/TableHead';
import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func, object } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';
import companyShape from 'shapes/company';
import employeeShape from 'shapes/employee';
import arrayToObject from 'utils/arrayToObject';
import searchEmployees from 'utils/searchEmployees';
import EmployeeRow from './EmployeeRow';
import Toolbar from './Toolbar';

const rootSx = {
  width: '100%',
  overflowX: 'auto',
  marginTop: 3,
  display: 'flex',
  flexDirection: 'column',
};

const tableWrapperSx = {
  overflowX: 'auto',
};

const tableSx = theme => ({
  borderTop: `1px solid ${theme.palette.divider}`,
});

class PeoplesTable extends React.Component {
  state = {
    order: {
      by: 'name',
      isReverse: false,
    },
    filteredIds: [],
    page: 0,
    rowsPerPage: 10,
    searchString: '',
  };

  columns = [
    {
      key: 'name',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Name'),
      xsVisible: true,
    },
    { key: 'code', disablePadding: false, label: I18n.t('ChannelDrawer.EmployeesTable.Code') },
    {
      key: 'department',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Department'),
    },
    {
      key: 'jobTitle',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Job Position'),
    },
    {
      key: 'location',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Location'),
    },
    {
      key: 'workedHours',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Worked Hours'),
    },
    {
      key: 'distribution',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Hour distribution'),
    },
    {
      key: 'actions',
      disablePadding: false,
      label: I18n.t('ChannelDrawer.EmployeesTable.Actions'),
    },
  ];

  sort = by => {
    const { order } = this.state;

    this.setState({
      order: { by, isReverse: !order.isReverse },
    });
  };

  search = searchString => {
    const { employees } = this.props;
    const filteredIds = searchEmployees(searchString, employees);

    this.setState({ searchString, filteredIds, page: 0 });
  };

  changePage = ({ selected: page }) => {
    this.setState({ page });
  };

  changeRowsPerPage = e => {
    this.setState({ rowsPerPage: e.target.value });
  };

  filterEmployees = employees => {
    const { filteredIds, searchString } = this.state;
    if (isEmpty(filteredIds) && isEmpty(searchString)) return employees;
    if (isEmpty(filteredIds) && !isEmpty(searchString)) return [];

    return filteredIds.map(_id => arrayToObject(employees)[_id]);
  };

  sortEmployees = employees => {
    const { order } = this.state;
    const sortFunction = (first, second) => {
      const f = first[order.by];
      const s = second[order.by];
      let ret = 0;
      if (typeof f === typeof s && typeof f === 'string') {
        ret = f.localeCompare(s);
      } else if (typeof f === typeof s && typeof f === 'undefined') {
        ret = 0;
      } else if (typeof f === 'undefined') {
        ret = -1;
      } else if (typeof s === 'undefined') {
        ret = 1;
      } else if (typeof f === typeof s && typeof Array.isArray(f)) {
        ret = f.sort(sortFunction).join(' ').localeCompare(s.sort(sortFunction).join(' '));
      }
      return order.isReverse ? -ret : ret;
    };
    const sorted = employees.sort(sortFunction);
    return sorted;
  };

  paginateEmployees = employees => {
    const { page, rowsPerPage } = this.state;
    return employees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  render() {
    const {
      additionalControls,
      employees,
      company,
      onExport,
      complianceEnabled,
      onGenerateReports,
      isGenerating,
    } = this.props;

    const { order, rowsPerPage, page } = this.state;
    const employeesCount = employees.length;

    const filtered = this.filterEmployees(employees);
    const sorted = this.sortEmployees(filtered);
    const paginated = this.paginateEmployees(sorted);

    return (
      <Box sx={rootSx}>
        <Toolbar
          onSearch={this.search}
          tab="TimeTracking"
          onExport={onExport}
          onGenerateReports={onGenerateReports}
          complianceEnabled={complianceEnabled}
          isGenerating={isGenerating}
        >
          {additionalControls}
        </Toolbar>
        <Box sx={tableWrapperSx}>
          <Table sx={tableSx}>
            <TableHead order={order} onSort={this.sort} columns={this.columns} />
            <TableBody>
              {paginated.map(
                employee =>
                  employee && (
                    <EmployeeRow
                      key={employee._id}
                      company={company}
                      employee={employee}
                      complianceEnabled={complianceEnabled}
                    />
                  ),
              )}
            </TableBody>
          </Table>
        </Box>
        <Pagination
          page={page}
          perPage={rowsPerPage}
          total={employeesCount}
          onChange={this.changePage}
          onChangePerPage={this.changeRowsPerPage}
        />
      </Box>
    );
  }
}

PeoplesTable.propTypes = {
  company: companyShape.isRequired,
  employees: arrayOf(employeeShape).isRequired,
  additionalControls: object,
  onExport: func.isRequired,
  complianceEnabled: bool,
  onGenerateReports: func,
  isGenerating: bool,
};

PeoplesTable.defaultProps = {
  additionalControls: null,
  complianceEnabled: false,
  onGenerateReports: null,
  isGenerating: false,
};

export default PeoplesTable;
